import React, { useState, useEffect } from 'react';
import PrimaryButton from '../../Common/PrimaryButton';

/**
 * Formulário para criar ou editar vacina
 */
const VacinaForm = ({ onClose, onSubmit, initialData = {} }) => {
    const isEditing = !!initialData.id;
    const [formData, setFormData] = useState({
        nome_vacina: '',
        data_aplicacao: '',
        lote: '',
        local_aplicacao: '',
        profissional_aplicador: '',
        proxima_dose: '',
        fabricante: '',
        dose_numero: '',
        via_administracao: '',
        reacao_adversa: '',
        observacoes: ''
    });
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isEditing && initialData) {
            setFormData({
                nome_vacina: initialData.nome_vacina || '',
                data_aplicacao: initialData.data_aplicacao 
                    ? new Date(initialData.data_aplicacao).toISOString().substring(0, 10) 
                    : '',
                lote: initialData.lote || '',
                local_aplicacao: initialData.local_aplicacao || '',
                profissional_aplicador: initialData.profissional_aplicador || '',
                proxima_dose: initialData.proxima_dose 
                    ? new Date(initialData.proxima_dose).toISOString().substring(0, 10) 
                    : '',
                fabricante: initialData.fabricante || '',
                dose_numero: initialData.dose_numero || '',
                via_administracao: initialData.via_administracao || '',
                reacao_adversa: initialData.reacao_adversa || '',
                observacoes: initialData.observacoes || ''
            });
        }
        setFormError(null);
    }, [initialData, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);

        if (!formData.nome_vacina.trim()) {
            setFormError('O nome da vacina é obrigatório.');
            return;
        }

        if (!formData.data_aplicacao) {
            setFormError('A data de aplicação é obrigatória.');
            return;
        }

        if (formData.proxima_dose && formData.data_aplicacao) {
            const aplicacao = new Date(formData.data_aplicacao);
            const proxima = new Date(formData.proxima_dose);
            if (proxima <= aplicacao) {
                setFormError('A próxima dose deve ser posterior à data de aplicação.');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            await onSubmit(formData);
            onClose();
        } catch (error) {
            setFormError(error?.response?.data?.message || error?.message || 'Erro ao salvar vacina.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm" role="alert">
                    {formError}
                </div>
            )}

            <div>
                <label htmlFor="nome_vacina" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Vacina <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="nome_vacina"
                    name="nome_vacina"
                    value={formData.nome_vacina}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ex: BCG, Hepatite B, Tríplice Viral..."
                    required
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="data_aplicacao" className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Aplicação <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="data_aplicacao"
                        name="data_aplicacao"
                        value={formData.data_aplicacao}
                        onChange={handleChange}
                        max={new Date().toISOString().substring(0, 10)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        required
                    />
                </div>

                <div>
                    <label htmlFor="dose_numero" className="block text-sm font-medium text-gray-700 mb-1">
                        Número da Dose
                    </label>
                    <input
                        type="number"
                        id="dose_numero"
                        name="dose_numero"
                        value={formData.dose_numero}
                        onChange={handleChange}
                        min="1"
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Ex: 1, 2, 3..."
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="lote" className="block text-sm font-medium text-gray-700 mb-1">
                        Lote
                    </label>
                    <input
                        type="text"
                        id="lote"
                        name="lote"
                        value={formData.lote}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Número do lote..."
                    />
                </div>

                <div>
                    <label htmlFor="fabricante" className="block text-sm font-medium text-gray-700 mb-1">
                        Fabricante
                    </label>
                    <input
                        type="text"
                        id="fabricante"
                        name="fabricante"
                        value={formData.fabricante}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Nome do fabricante..."
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="local_aplicacao" className="block text-sm font-medium text-gray-700 mb-1">
                        Local de Aplicação
                    </label>
                    <input
                        type="text"
                        id="local_aplicacao"
                        name="local_aplicacao"
                        value={formData.local_aplicacao}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Ex: UBS, Clínica, Hospital..."
                    />
                </div>

                <div>
                    <label htmlFor="profissional_aplicador" className="block text-sm font-medium text-gray-700 mb-1">
                        Profissional Aplicador
                    </label>
                    <input
                        type="text"
                        id="profissional_aplicador"
                        name="profissional_aplicador"
                        value={formData.profissional_aplicador}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Nome do profissional..."
                    />
                </div>
            </div>

            <div>
                <label htmlFor="via_administracao" className="block text-sm font-medium text-gray-700 mb-1">
                    Via de Administração
                </label>
                <select
                    id="via_administracao"
                    name="via_administracao"
                    value={formData.via_administracao}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                >
                    <option value="">Selecione...</option>
                    <option value="Intramuscular">Intramuscular</option>
                    <option value="Subcutânea">Subcutânea</option>
                    <option value="Oral">Oral</option>
                    <option value="Intranasal">Intranasal</option>
                    <option value="Intradérmica">Intradérmica</option>
                </select>
            </div>

            <div>
                <label htmlFor="proxima_dose" className="block text-sm font-medium text-gray-700 mb-1">
                    Próxima Dose
                </label>
                <input
                    type="date"
                    id="proxima_dose"
                    name="proxima_dose"
                    value={formData.proxima_dose}
                    onChange={handleChange}
                    min={formData.data_aplicacao || new Date().toISOString().substring(0, 10)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                />
            </div>

            <div>
                <label htmlFor="reacao_adversa" className="block text-sm font-medium text-gray-700 mb-1">
                    Reação Adversa
                </label>
                <textarea
                    id="reacao_adversa"
                    name="reacao_adversa"
                    value={formData.reacao_adversa}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Descreva qualquer reação adversa observada..."
                />
            </div>

            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Salvar')}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default VacinaForm;

